import React, { useRef, useState } from 'react';
import { useLibraryItemDetails } from './hooks/useLibraryItemDetails';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Input,
  Modal,
  Tooltip,
  Typography,
} from '@mui/material';
import styles from './LibraryItemDetails.module.scss';
import { ROUTES } from '../../common/constants/routes';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import BrushIcon from '../../../static/assets/svg/brush.svg';
import { ProgressOverlay } from '../../common/progress-overlay/ProgressOverlay';
import ContractGenerator from '../../common/contract-generator/ContractGenerator';
import { useTranslation } from 'react-i18next';
import FileTypeDownloadModal from '../../common/file-type-download-modal/FileTypeDownloadModal';
import { useFileTypeDownload } from '../../common/file-type-download-modal/useFileTypeDownload';
import FileSaveIcon from '../../../static/assets/svg/file_save.svg';
import { LibraryItem } from '../library/components/library-list/types';
import { useAppSelector } from '../../common/hooks/useAppSelector';
import { selectAllDocuments } from '../../slices/documents/documentsSlice';
import { useLibraryFunctions } from '../library/hooks/useLibraryFunctions';
import { useDeleteLibraryItemMessageModal } from '../library/hooks/useDeleteMessageModal';
import MessageModal from '../../common/message-modal/MessageModal';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import DisclaimerText from '../../common/disclaimer-text/DisclaimerText';
import { acceptDisclaimer, selectDisclaimerChecked } from '../../slices/disclaimer/disclaimerSlice';
import { useAppDispatch } from '../../common/hooks/useAppDispatch';
import { selectAllCompanies } from '../../slices/companies/companiesSlice';
import ContentCopyIcon from '../../../static/assets/svg/content_copy.svg';
import { selectUser } from '../../slices/user/userSlice';
import JoditEditor from 'jodit-react';
import { Helpers } from '../../common/helpers/helpers';
import { toast } from 'react-toastify';
import { showMessage } from '../../slices/show-plan-upgrading-message/showPlanUpgradingMessage';

const LibraryItemDetails = () => {
  const [copyModalOpen, setCopyModalOpen] = useState<LibraryItem | null>(null);
  const documentUploads = useAppSelector(selectAllDocuments);
  const {
    dlHTML,
    setDLHTML,
    templateTitle,
    editMode,
    setEditMode,
    loadingHTML,
    contractCreationEnabled,
    setContractCreationEnabled,
    templateCategory,
    templateItem,
    handleSave,
    loading: savingTemplate,
  } = useLibraryItemDetails();
  const {
    setModalOpen: setFileTypeModalOpen,
    modalOpen: fileTypeModalOpen,
    handleCloseModal: handleCloseFileTypeModal,
    libraryItem: libraryItemForDownloading,
    setLibraryItem: setLibraryItemForDownloading,
    setFileName: setFileNameForDownloading,
    fileName: fileNameForDownloading,
    fileType: fileTypeForDownloading,
    setFileType: setFileTypeForDownloading,
    handleTemplateDownload,
    pendingStatus: downloadPendingStatus,
  } = useFileTypeDownload();
  const {
    handleS3DownloadLink,
    disclaimerMessage,
    setDisclaimerMessage,
    handleLibraryItemCopy,
    handleTemplateCopy,
  } = useLibraryFunctions();
  const {
    modalOpen: deleteLibraryItemModalOpen,
    setModalOpen: setDeleteLibraryItemModalOpen,
    handleDeleteDocumentTemplate,
    libraryItemToDelete,
    setLibraryItemToDelete,
    pendingStatus: deletingLibraryItemPending,
  } = useDeleteLibraryItemMessageModal();
  const currentUser = useAppSelector(selectUser);
  const { t } = useTranslation();
  const location = useLocation();
  const path = location.pathname;
  const dispatch = useAppDispatch();
  const editor = useRef(null);
  const company = useAppSelector(selectAllCompanies)[0];
  const navigate = useNavigate();
  const disclaimerAccepted = useAppSelector(selectDisclaimerChecked);
  const disclaimerStatus = useAppSelector((state) => state.disclaimerInfo.status);
  const freePlan = !company?.stripe_product_id && !company?.plan_settings.stripe_product_id;

  const templateLabels =
    templateItem && 'region' in templateItem
      ? {
          categoryLabel: templateItem.category,
          typeLabel: templateItem.dl_types[0],
          workflowLabel: templateItem.dl_workflows[0],
        }
      : null;

  const CopyModal: React.FC<{ item: LibraryItem }> = ({ item }) => {
    const handleCopying = (newname: string) => {
      const data = {
        name: newname,
        region: item.region,
        dt_workflows: item.dl_workflows,
        dt_types: item.dl_types,
        user: currentUser.id,
      };

      try {
        handleLibraryItemCopy(data, item.id); // Wait for the copy operation to complete
        setCopyModalOpen(null); // Close the modal after the operation finishes
      } catch (error) {
        if (
          error.response?.data?.errors?.length > 0 &&
          error.response?.data?.errors[0] === t('messages.exceededPlanLimits')
        ) {
          dispatch(showMessage(t('messages.monthLimitReached')));
        } else {
          const errorMessage = Helpers.returnErrorText(error);
          toast.error(errorMessage.toString());
        }
      }
    };
    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const formData = new FormData(e.currentTarget);
      const newTemplateName = formData.get('newTemplateName') as string;
      if ('region' in item) {
        handleCopying(newTemplateName);
      } else {
        const updatedItem = { ...item, name: newTemplateName };
        try {
          handleTemplateCopy(updatedItem, currentUser.id);
          setCopyModalOpen(null);
        } catch (error) {
          if (
            error.response?.data?.errors?.length > 0 &&
            error.response?.data?.errors[0] === t('messages.exceededPlanLimits')
          ) {
            dispatch(showMessage(t('messages.monthLimitReached')));
          } else {
            const errorMessage = Helpers.returnErrorText(error);
            toast.error(errorMessage.toString());
          }
        }
      }
    };
    return (
      <Modal
        open={copyModalOpen?.id === item.id}
        className={styles.copymodal}
        onClose={() => setCopyModalOpen(null)}
      >
        <Grid container sx={{ boxShadow: 'none', border: 'none', outline: 'none' }}>
          <Grid item xs={12} sm={11} md={11} lg={11}>
            <Card>
              <CardContent sx={{ paddingBottom: '16px !important' }}>
                <Typography variant={'h6'} gutterBottom>
                  Copy to Own library
                </Typography>

                <Typography>Please change the template name if necessary.</Typography>
                <form onSubmit={handleSubmit}>
                  <Box sx={{ maxHeight: '500px', overflow: 'auto' }}>
                    <Input
                      name='newTemplateName'
                      placeholder={item.name ? item.name : t('templatePage.headerDefault')}
                      defaultValue={`${
                        item.name ? item.name : t('templatePage.headerDefault')
                      } - ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}`}
                      className={styles.input}
                      fullWidth
                      required
                    />
                  </Box>

                  <Grid container justifyContent='flex-end' gap={'8px'} mt={2}>
                    <Button
                      variant='contained'
                      onClick={() => setCopyModalOpen(null)}
                      className={'btn-default btn-action-basic btn-cancel'}
                    >
                      Go back
                    </Button>

                    <Button
                      variant='contained'
                      type='submit'
                      className='btn-default btn-action-basic'
                    >
                      Confirm
                    </Button>
                  </Grid>
                </form>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Modal>
    );
  };

  const handleGenerateContract = () => {
    const actionAvailableFree = 'is_free' in templateItem && templateItem.is_free;
    if ((freePlan && actionAvailableFree) || !freePlan || !('region' in templateItem)) {
      if (disclaimerAccepted) {
        setContractCreationEnabled(true);
      } else {
        setDisclaimerMessage(true);
      }
    } else if (freePlan && !actionAvailableFree) {
      dispatch(showMessage(t('messages.')));
    }
  };
  const handleDownloading = (item: LibraryItem) => {
    if (!item.download_link) {
      setFileNameForDownloading(
        'name' in item
          ? item.name
          : documentUploads
              ?.find((el) => el.id === item.document_upload)
              ?.name?.replace('.pdf', '')
              .replace('.docx', '')
              .replace('.doc', '')
      );
      setLibraryItemForDownloading(item);
      setFileTypeModalOpen(true);
    } else {
      handleS3DownloadLink(item.id, 'name' in item ? item.name : '');
    }
  };
  return (
    <>
      <Box>
        <Grid container className={styles.header}>
          <Grid item xs={6}>
            <Typography variant='h5' component='h1' className={styles.title}>
              {t('headers.workspace')}
            </Typography>
            <Box className={styles.breadcrumbs}>
              <Link className={styles.documentViewText} to={ROUTES.WORKSPACE}>
                {t('headers.workspace')}
              </Link>
              <Typography variant='body2' className={styles.separator}>
                ›
              </Typography>
              <Typography variant='body2' className={styles.documentViewText}>
                {templateTitle}
              </Typography>
            </Box>
          </Grid>
        </Grid>
        <Grid container justifyContent='space-between' spacing={2} className='inputs-editable'>
          <Grid
            item
            xs={12}
            className={styles.gridItem}
            sx={{ ml: '16px', position: 'relative', paddingRight: '16px' }}
          >
            <Box
              display='flex'
              paddingBottom='16px'
              paddingRight='8px'
              justifyContent='space-between'
              alignItems='center'
            >
              <Box>
                <Grid container item className='mb-1 pt-0 pb-0 ps-0 pe-0'>
                  <Typography variant='h2' className={styles.title}>
                    {templateTitle}
                  </Typography>
                </Grid>
                {templateLabels && (
                  <Grid container item className={styles.labels_row}>
                    {templateLabels && templateLabels.categoryLabel
                      ? templateLabels.categoryLabel
                      : null}
                    {templateLabels && templateLabels.workflowLabel
                      ? ' / ' + templateLabels.workflowLabel
                      : null}
                    {templateLabels && templateLabels.typeLabel
                      ? ' / ' + templateLabels.typeLabel
                      : null}
                  </Grid>
                )}
              </Box>

              <Box display='flex' alignItems='flex-start'>
                <Button
                  style={{ marginRight: '8px', flexShrink: 0 }}
                  type='button'
                  className='btn-default btn-small btn-action-basic'
                  role='button'
                  variant='contained'
                  onClick={() => {
                    handleGenerateContract();
                  }}
                  disabled={contractCreationEnabled}
                >
                  {t('buttons.generateContract')}
                </Button>
                {templateCategory === 'company-templates' ? (
                  editMode ? (
                    <Button
                      style={{ marginRight: '8px' }}
                      type='button'
                      className='btn-default btn-small btn-action-basic'
                      role='button'
                      variant='contained'
                      onClick={() => {
                        handleSave();
                      }}
                    >
                      {t('buttons.save')}
                    </Button>
                  ) : (
                    <Tooltip title={t('buttons.edit')} placement='top' arrow>
                      <IconButton
                        size='small'
                        className={styles.editButton}
                        onClick={() => setEditMode(true)}
                      >
                        <BrushIcon />
                      </IconButton>
                    </Tooltip>
                  )
                ) : null}
                <Tooltip title={t('buttons.download')} placement='top' arrow>
                  <IconButton
                    size='small'
                    className={styles.actionButton}
                    onClick={() => handleDownloading(templateItem)}
                  >
                    <FileSaveIcon />
                  </IconButton>
                </Tooltip>
                {!templateItem?.download_link && (
                  <Tooltip title={t('buttons.copyDocument')} placement='top' arrow>
                    <IconButton
                      size='small'
                      onClick={() => {
                        const actionAvailableFree =
                          'is_free' in templateItem && templateItem.is_free;
                        if (freePlan || !templateItem.region) {
                          if (actionAvailableFree || !templateItem.region) {
                            setCopyModalOpen(templateItem);
                          } else {
                            dispatch(showMessage(t('messages.considerStartingPlanLibrary')));
                          }
                        } else {
                          setCopyModalOpen(null);
                        }
                      }}
                      className={styles.action_button}
                    >
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                )}
                {!templateItem?.region && (
                  <Tooltip title={t('messages.delete')} placement='top' arrow>
                    <IconButton
                      size='small'
                      aria-label='delete'
                      onClick={() => {
                        setLibraryItemToDelete(templateItem);
                        setDeleteLibraryItemModalOpen(true);
                      }}
                    >
                      <DeleteOutlineOutlinedIcon
                        style={{ marginRight: 0, fill: '#152a58', width: '20px', height: '20px' }}
                      />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
            <Box
              className={styles.tabContent}
              sx={{ height: `calc(100vh - 220px)`, overflow: loadingHTML ? 'hidden' : 'auto' }}
            >
              <Box className={styles.mainContent}>
                {contractCreationEnabled && (
                  <Box width='45%'>
                    <ContractGenerator
                      templateTitle={templateTitle}
                      libraryItem={templateItem}
                      templateId={templateItem?.id}
                      setContractCreationEnabled={setContractCreationEnabled}
                    />
                  </Box>
                )}
                <div
                  className={`${styles.main_container} ${
                    contractCreationEnabled ? styles.w_55 : ''
                  }`}
                >
                  <div
                    id='editor'
                    className='main-editor-container'
                    style={{ height: `calc(100vh - 220px)` }}
                  >
                    {/*editMode ? (
                      <FroalaEditorComponent
                        tag='textarea'
                        model={dlHTML}
                        config={{
                          key: 'froala',
                          height: 420,
                          immediateReactModelUpdate: true,
                        }}
                        onModelChange={(event: any) => setDLHTML(event)}
                      />
                    ) : (
                      <FroalaEditorView model={dlHTML} />
                    )*/}
                    <JoditEditor
                      ref={editor}
                      config={{
                        readonly: !editMode,
                        buttons:
                          'bold,italic,underline,strikethrough,eraser,ul,ol,font,fontsize,paragraph,lineHeight,copy,source,left,right,center,image,paste,selectall,hr,table,link,symbols,indent,print,outdent',
                      }}
                      value={dlHTML}
                      onBlur={(newContent) => setDLHTML(newContent)} // preferred to use only this option to update the content for performance reasons
                    />
                  </div>
                </div>
              </Box>
            </Box>
            {(loadingHTML || disclaimerStatus === 'loading' || savingTemplate) && (
              <ProgressOverlay className={'position-absolute'} />
            )}
          </Grid>
        </Grid>
      </Box>
      {fileTypeModalOpen && libraryItemForDownloading && (
        <FileTypeDownloadModal
          onClose={() => {
            handleCloseFileTypeModal();
          }}
          open={fileTypeModalOpen}
          fileName={fileNameForDownloading}
          fileType={fileTypeForDownloading}
          handleFileTypeChange={setFileTypeForDownloading}
          handleFileNameChange={setFileNameForDownloading}
          libraryItem={libraryItemForDownloading}
          handleDownloadLibraryHTML={() => {
            handleTemplateDownload(
              libraryItemForDownloading.id,
              fileTypeForDownloading,
              fileNameForDownloading
            );
          }}
        />
      )}
      {deleteLibraryItemModalOpen && libraryItemToDelete && (
        <MessageModal
          open={deleteLibraryItemModalOpen}
          modalTitle={t('dialogTitles.deleteTemplate')}
          modalMessage={t('messages.deleteTemplate')}
          actionButtonText={t('buttons.goBack')}
          actionButtonFn={() => {
            setLibraryItemToDelete(null);
            setDeleteLibraryItemModalOpen(false);
          }}
          goBackButtonText={t('buttons.delete')}
          document={libraryItemToDelete}
          onClose={() => {
            handleDeleteDocumentTemplate();
          }}
        />
      )}
      {disclaimerMessage && (
        <MessageModal
          open={disclaimerMessage}
          modalTitle={t('headers.documentTerms')}
          modalContent={<DisclaimerText />}
          actionButtonText={t('buttons.accept')}
          headerVariant={'h5'}
          actionButtonFn={() => {
            dispatch(acceptDisclaimer())
              .unwrap()
              .then(() => {
                setDisclaimerMessage(false);
                setContractCreationEnabled(true);
                //setGeneratorOpened(true);
              });
          }}
          goBackButtonText={t('buttons.goBack')}
          onClose={() => {
            setDisclaimerMessage(false);
          }}
        />
      )}
      {copyModalOpen && <CopyModal item={templateItem} />}
      {(downloadPendingStatus ||
        deletingLibraryItemPending ||
        disclaimerStatus === 'loadingAccepting') && <ProgressOverlay />}
    </>
  );
};

export default LibraryItemDetails;
